import Graph from 'graphology';
import { useEffect } from 'react';
import Sigma from 'sigma';

import {
  CLUSTERING_AUTO_HIDE_RELATIONSHIPS,
  CLUSTERING_RELATED_ATTRIBUTES,
} from '@/components/nodeGraph/config/graphConfig';
import { DefaultVisibilityState } from '@/components/nodeGraph/utils/constants';
import { VisibilityState } from '@/components/nodeGraph/utils/types';
import { ResourceType } from '@/types';

export const useGraphAttributeVisibility = ({
  visibilityState,
  selectedNodes,
  sigma,
  graphRef,
  activeClusterAttribute,
}: {
  visibilityState: VisibilityState;
  selectedNodes: Set<string>;
  sigma: Sigma;
  graphRef: Graph | null;
  activeClusterAttribute?: string | null;
}) => {
  useEffect(() => {
    if (!sigma || !graphRef) return;
    const graph = sigma.getGraph();

    let hasChanges = false;
    graph.forEachNode(nodeId => {
      const entityType = graph.getNodeAttribute(nodeId, 'entityType');
      const label = graph.getNodeAttribute(nodeId, 'label');

      if (
        !DefaultVisibilityState[
          entityType as keyof typeof DefaultVisibilityState
        ]
      ) {
        return;
      }

      let shouldHide = !visibilityState[entityType];

      // Auto-hide relationship nodes during attribute clustering (configurable)
      if (
        CLUSTERING_AUTO_HIDE_RELATIONSHIPS &&
        activeClusterAttribute &&
        (entityType === ResourceType.Seed ||
          entityType === ResourceType.Risk ||
          entityType === ResourceType.Technology ||
          entityType === ResourceType.Webpage ||
          entityType === 'Tag')
      ) {
        shouldHide = true;
      }

      // Handle attribute visibility (both entity visibility and individual attribute visibility)
      if (entityType === ResourceType.Attribute) {
        const attributeName = label.split(':')[0];

        // Get related attributes to show with this clustering mode (from config)
        const relatedAttributes = activeClusterAttribute
          ? CLUSTERING_RELATED_ATTRIBUTES[activeClusterAttribute] ||
            CLUSTERING_RELATED_ATTRIBUTES.default
          : [];

        // When clustering by a specific attribute, hide all other attribute nodes
        // EXCEPT for configured related contextual attributes
        if (
          activeClusterAttribute &&
          attributeName !== activeClusterAttribute
        ) {
          // Check if this attribute is in the related attributes list for current clustering mode
          if (relatedAttributes.includes(attributeName)) {
            // Related attribute: respect user visibility settings
            shouldHide =
              !visibilityState[ResourceType.Attribute] ||
              !visibilityState[attributeName];
          } else {
            // Not related: auto-hide to reduce clutter
            shouldHide = true;
          }
        } else {
          // This is the clustering attribute itself, or no clustering active
          // Hide if either the Attribute entity type is hidden OR the specific attribute is hidden
          shouldHide =
            !visibilityState[ResourceType.Attribute] ||
            !visibilityState[attributeName];
        }
      }

      const oldHidden = graph.getNodeAttribute(nodeId, 'hidden') || false;
      if (oldHidden !== shouldHide) {
        graph.setNodeAttribute(nodeId, 'hidden', shouldHide);
        hasChanges = true;
      }
    });

    // Only refresh if we actually made changes
    if (hasChanges) {
      requestAnimationFrame(() => {
        sigma?.refresh();
      });
    }
  }, [
    sigma,
    graphRef,
    JSON.stringify(visibilityState),
    selectedNodes,
    activeClusterAttribute,
  ]);
};
